/**
 * Interactive Annotation Overlay
 * Supports selection, dragging, resizing, and editing of annotations
 */

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { TOOL_TYPES, FONT_PRESETS, SIZE_PRESETS } from './MarkupEditorToolbar';

// Minimum dimensions for annotations
const MIN_WIDTH = 42;
const MIN_HEIGHT = 18;
const HANDLE_SIZE = 8;
const SNAP_THRESHOLD = 10;

// Page boundary constants
const PAGE_MARGIN = 10;
const MIN_RECT_SPACING = 8;
const MAX_BOX_WIDTH = 500;  // Reject placeholder regions
const MAX_BOX_HEIGHT = 80;  // Reject section bands

// PRIMARY ANNOTATION COLOR: DARKER RED for readability
const ANNOTATION_COLOR = '#B91C1C';  // Darker red
const ANNOTATION_COLOR_LABEL = '#991B1B';  // Even darker for labels
const ANNOTATION_COLOR_HOVER = '#EF4444';  // Secondary red for hover

// Get color - ALL annotations use darker RED for better visibility
function getSeverityColor(severity) {
  return ANNOTATION_COLOR_LABEL;
}

/**
 * Validate box dimensions - reject placeholder regions
 */
function isValidBoxSize(width, height) {
  if (width > MAX_BOX_WIDTH || height > MAX_BOX_HEIGHT) return false;
  if (width < MIN_WIDTH || height < MIN_HEIGHT) return false;
  const aspectRatio = width / height;
  if (aspectRatio > 20) return false;  // Too wide = placeholder
  return true;
}

/**
 * Constrain coordinates to stay within page bounds
 */
function constrainToPage(x, y, width, height, pageWidth, pageHeight) {
  const margin = PAGE_MARGIN;
  return {
    x: Math.max(margin, Math.min(pageWidth - margin - width, x)),
    y: Math.max(margin, Math.min(pageHeight - margin - height, y)),
    width: Math.min(width, pageWidth - margin * 2),
    height: Math.min(height, pageHeight - margin * 2)
  };
}

/**
 * Find optimal label position inside page bounds
 */
function findLabelPositionInPage(boxX, boxY, boxWidth, boxHeight, labelWidth, labelHeight, pageWidth, pageHeight) {
  const margin = PAGE_MARGIN;
  
  // Preferred positions: right, above, below, left
  const positions = [
    { x: boxX + boxWidth + 12, y: boxY },
    { x: boxX, y: boxY - labelHeight - 8 },
    { x: boxX, y: boxY + boxHeight + 8 },
    { x: boxX - labelWidth - 12, y: boxY },
  ];
  
  for (const pos of positions) {
    if (pos.x >= margin && pos.x + labelWidth <= pageWidth - margin &&
        pos.y >= margin && pos.y + labelHeight <= pageHeight - margin) {
      return pos;
    }
  }
  
  // Fallback: clamp to page
  return {
    x: Math.max(margin, Math.min(pageWidth - margin - labelWidth, boxX + boxWidth + 8)),
    y: Math.max(margin, Math.min(pageHeight - margin - labelHeight, boxY))
  };
}

// Get font style from preset - DEFAULT to SMALL size for text notes
function getFontStyle(fontPresetId, sizePresetId) {
  const font = FONT_PRESETS[fontPresetId?.toUpperCase()] || FONT_PRESETS.LEGAL_SANS;
  // Default to SMALL size, not STANDARD
  const size = SIZE_PRESETS[sizePresetId?.toUpperCase()] || SIZE_PRESETS.SMALL;
  return {
    fontFamily: font.fontFamily,
    fontWeight: font.fontWeight,
    fontSize: size.fontSize
  };
}

/**
 * Resize Handle Component (SVG-based)
 */
const ResizeHandle = ({ position, boxX, boxY, boxWidth, boxHeight, onMouseDown }) => {
  const positionCoords = {
    nw: { x: boxX - HANDLE_SIZE/2, y: boxY - HANDLE_SIZE/2 },
    ne: { x: boxX + boxWidth - HANDLE_SIZE/2, y: boxY - HANDLE_SIZE/2 },
    sw: { x: boxX - HANDLE_SIZE/2, y: boxY + boxHeight - HANDLE_SIZE/2 },
    se: { x: boxX + boxWidth - HANDLE_SIZE/2, y: boxY + boxHeight - HANDLE_SIZE/2 },
    n: { x: boxX + boxWidth/2 - HANDLE_SIZE/2, y: boxY - HANDLE_SIZE/2 },
    s: { x: boxX + boxWidth/2 - HANDLE_SIZE/2, y: boxY + boxHeight - HANDLE_SIZE/2 },
    e: { x: boxX + boxWidth - HANDLE_SIZE/2, y: boxY + boxHeight/2 - HANDLE_SIZE/2 },
    w: { x: boxX - HANDLE_SIZE/2, y: boxY + boxHeight/2 - HANDLE_SIZE/2 }
  };
  
  const cursors = {
    nw: 'nw-resize', ne: 'ne-resize', sw: 'sw-resize', se: 'se-resize',
    n: 'n-resize', s: 's-resize', e: 'e-resize', w: 'w-resize'
  };

  const coords = positionCoords[position];
  
  return (
    <rect
      x={coords.x}
      y={coords.y}
      width={HANDLE_SIZE}
      height={HANDLE_SIZE}
      fill="white"
      stroke="#10b981"
      strokeWidth={2}
      style={{ cursor: cursors[position] }}
      onMouseDown={(e) => {
        e.stopPropagation();
        onMouseDown(position, e);
      }}
    />
  );
};

/**
 * Arrow Endpoint Handle
 */
const ArrowEndpointHandle = ({ x, y, type, onMouseDown }) => (
  <circle
    cx={x}
    cy={y}
    r={6}
    fill={type === 'from' ? '#10b981' : '#ef4444'}
    stroke="white"
    strokeWidth={2}
    style={{ cursor: 'move' }}
    onMouseDown={(e) => {
      e.stopPropagation();
      onMouseDown(type, e);
    }}
  />
);

/**
 * Interactive Annotation Item
 */
const InteractiveAnnotationItem = ({
  annotation,
  isSelected,
  isHighlighted = false,
  isEditMode,
  scaleFactor,
  zoom = 1,
  onSelect,
  onUpdate,
  onDelete,
  activeTool
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingLabel, setIsDraggingLabel] = useState(false);  // Label-specific drag
  const [isResizing, setIsResizing] = useState(false);
  const [resizeHandle, setResizeHandle] = useState(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [originalPosition, setOriginalPosition] = useState(null);

  // Scale: convert from document coordinates to render coordinates
  const scale = (val) => Math.round((Number(val) || 0) * scaleFactor);
  
  // Unscale: convert from screen delta to document coordinates
  // IMPORTANT: Must account for CSS zoom transform when converting mouse movements
  // Screen pixels move at zoom rate, so divide by (scaleFactor * zoom) to get document units
  const unscale = (val) => Math.round((Number(val) || 0) / (scaleFactor * zoom));

  const strokeColor = getSeverityColor(annotation.severity);
  const isStructural = annotation.annotation_type === 'dashed_rectangle' || 
                       annotation.annotation_scope === 'structural';
  const targetRegion = annotation.target_region || {};
  const labelPos = annotation.label_position || {};
  const arrowFrom = annotation.arrow_from || {};
  const arrowTo = annotation.arrow_to || {};

  // Scaled dimensions
  const boxX = scale(targetRegion.x || 0);
  const boxY = scale(targetRegion.y || 0);
  const boxWidth = Math.max(scale(targetRegion.width || 150), MIN_WIDTH);
  const boxHeight = Math.max(scale(targetRegion.height || 30), MIN_HEIGHT);
  const labelX = scale(labelPos.x || (targetRegion.x + targetRegion.width + 15));
  const labelY = scale(labelPos.y || targetRegion.y);

  // Handle mouse down on RECTANGLE for dragging whole annotation
  // IMPORTANT: Clicking existing objects ALWAYS selects them, regardless of active tool
  // This prevents accidental creation when user meant to edit existing annotation
  const handleRectMouseDown = (e) => {
    if (!isEditMode) return;
    
    e.stopPropagation();
    
    // Handle DELETE tool - delete on click
    if (activeTool === TOOL_TYPES.DELETE) {
      onDelete(annotation);
      return;
    }
    
    // EXISTING OBJECT PRIORITY: Always select when clicking an existing annotation
    // This overrides any active creation tool (Rectangle, Arrow, Text, etc.)
    onSelect(annotation);
    
    // If already selected, allow dragging (works with SELECT tool or after first click)
    if (isSelected || activeTool === TOOL_TYPES.SELECT) {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setOriginalPosition({
        target_region: { ...annotation.target_region },
        label_position: { ...annotation.label_position },
        arrow_from: { ...annotation.arrow_from },
        arrow_to: { ...annotation.arrow_to }
      });
    }
  };

  // Handle mouse down on LABEL for independent label dragging
  // IMPORTANT: Clicking label ALWAYS allows editing, regardless of active tool
  // This is the "direct label edit" interaction - no need to switch to Select first
  const handleLabelMouseDown = (e) => {
    if (!isEditMode) return;
    
    e.stopPropagation();
    
    // Handle DELETE tool
    if (activeTool === TOOL_TYPES.DELETE) {
      onDelete(annotation);
      return;
    }
    
    // EXISTING OBJECT PRIORITY: Always select and enable label editing
    // This overrides any active creation tool (Rectangle, Arrow, Text, etc.)
    // User expectation: "I clicked the label, so let me edit the label"
    onSelect(annotation);
    
    // Start label-only drag immediately (works regardless of active tool)
    setIsDraggingLabel(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setOriginalPosition({
      label_position: { 
        x: labelPos.x || (targetRegion.x + targetRegion.width + 15),
        y: labelPos.y || targetRegion.y
      }
    });
  };

  // Handle resize start
  const handleResizeStart = (handle, e) => {
    if (!isEditMode) return;
    setIsResizing(true);
    setResizeHandle(handle);
    setDragStart({ x: e.clientX, y: e.clientY });
    setOriginalPosition({
      target_region: { ...annotation.target_region }
    });
  };

  // Handle arrow endpoint drag start
  const handleArrowEndpointStart = (type, e) => {
    if (!isEditMode) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX, y: e.clientY });
    setOriginalPosition({
      [`arrow_${type}`]: { ...annotation[`arrow_${type}`] }
    });
    setResizeHandle(`arrow_${type}`);
  };

  // Handle mouse move for dragging/resizing
  useEffect(() => {
    if (!isDragging && !isResizing) return;

    const handleMouseMove = (e) => {
      const dx = unscale(e.clientX - dragStart.x);
      const dy = unscale(e.clientY - dragStart.y);

      if (isResizing && originalPosition) {
        const orig = originalPosition.target_region;
        let newRegion = { ...annotation.target_region };

        switch (resizeHandle) {
          case 'nw':
            newRegion.x = orig.x + dx;
            newRegion.y = orig.y + dy;
            newRegion.width = Math.max(orig.width - dx, MIN_WIDTH);
            newRegion.height = Math.max(orig.height - dy, MIN_HEIGHT);
            break;
          case 'ne':
            newRegion.y = orig.y + dy;
            newRegion.width = Math.max(orig.width + dx, MIN_WIDTH);
            newRegion.height = Math.max(orig.height - dy, MIN_HEIGHT);
            break;
          case 'sw':
            newRegion.x = orig.x + dx;
            newRegion.width = Math.max(orig.width - dx, MIN_WIDTH);
            newRegion.height = Math.max(orig.height + dy, MIN_HEIGHT);
            break;
          case 'se':
            newRegion.width = Math.max(orig.width + dx, MIN_WIDTH);
            newRegion.height = Math.max(orig.height + dy, MIN_HEIGHT);
            break;
          case 'n':
            newRegion.y = orig.y + dy;
            newRegion.height = Math.max(orig.height - dy, MIN_HEIGHT);
            break;
          case 's':
            newRegion.height = Math.max(orig.height + dy, MIN_HEIGHT);
            break;
          case 'e':
            newRegion.width = Math.max(orig.width + dx, MIN_WIDTH);
            break;
          case 'w':
            newRegion.x = orig.x + dx;
            newRegion.width = Math.max(orig.width - dx, MIN_WIDTH);
            break;
        }

        onUpdate({
          ...annotation,
          target_region: newRegion,
          edited_by_user: true,
          edit_type: 'resized'
        });
      } else if (isDragging && originalPosition) {
        if (resizeHandle === 'arrow_from') {
          onUpdate({
            ...annotation,
            arrow_from: {
              x: originalPosition.arrow_from.x + dx,
              y: originalPosition.arrow_from.y + dy
            },
            edited_by_user: true,
            edit_type: 'moved'
          });
        } else if (resizeHandle === 'arrow_to') {
          onUpdate({
            ...annotation,
            arrow_to: {
              x: originalPosition.arrow_to.x + dx,
              y: originalPosition.arrow_to.y + dy
            },
            edited_by_user: true,
            edit_type: 'moved'
          });
        } else {
          // Move entire annotation
          const newTargetRegion = {
            ...annotation.target_region,
            x: originalPosition.target_region.x + dx,
            y: originalPosition.target_region.y + dy
          };
          const newLabelPos = {
            x: (originalPosition.label_position?.x || 0) + dx,
            y: (originalPosition.label_position?.y || 0) + dy
          };
          const newArrowFrom = {
            x: (originalPosition.arrow_from?.x || 0) + dx,
            y: (originalPosition.arrow_from?.y || 0) + dy
          };
          const newArrowTo = {
            x: (originalPosition.arrow_to?.x || 0) + dx,
            y: (originalPosition.arrow_to?.y || 0) + dy
          };

          // Check if moved far enough to detach from original snippet
          const moveDistance = Math.sqrt(dx * dx + dy * dy);
          const manuallyDetached = moveDistance > 50;

          onUpdate({
            ...annotation,
            target_region: newTargetRegion,
            label_position: newLabelPos,
            arrow_from: newArrowFrom,
            arrow_to: newArrowTo,
            edited_by_user: true,
            edit_type: 'moved',
            manually_detached: manuallyDetached || annotation.manually_detached
          });
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
      setIsResizing(false);
      setResizeHandle(null);
      setOriginalPosition(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, isResizing, dragStart, originalPosition, annotation, onUpdate, resizeHandle, unscale]);

  // Handle LABEL-ONLY dragging - separate from rectangle
  useEffect(() => {
    if (!isDraggingLabel) return;

    const handleLabelMouseMove = (e) => {
      const dx = unscale(e.clientX - dragStart.x);
      const dy = unscale(e.clientY - dragStart.y);

      if (originalPosition?.label_position) {
        const newLabelPos = {
          x: originalPosition.label_position.x + dx,
          y: originalPosition.label_position.y + dy
        };

        // Auto-update arrow to connect from new label position to rectangle
        const newArrowFrom = {
          x: newLabelPos.x - 5,
          y: newLabelPos.y + 15
        };
        const newArrowTo = {
          x: (annotation.target_region?.x || 0) + (annotation.target_region?.width || 100) + 3,
          y: (annotation.target_region?.y || 0) + ((annotation.target_region?.height || 30) / 2)
        };

        onUpdate({
          ...annotation,
          label_position: newLabelPos,
          arrow_from: newArrowFrom,
          arrow_to: newArrowTo,
          edited_by_user: true,
          edit_type: 'label_moved'
        });
      }
    };

    const handleLabelMouseUp = () => {
      setIsDraggingLabel(false);
      setOriginalPosition(null);
    };

    document.addEventListener('mousemove', handleLabelMouseMove);
    document.addEventListener('mouseup', handleLabelMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleLabelMouseMove);
      document.removeEventListener('mouseup', handleLabelMouseUp);
    };
  }, [isDraggingLabel, dragStart, originalPosition, annotation, onUpdate, unscale]);

  // Label styling - MULTI-LINE support, NO TRUNCATION of law citations
  const fontStyle = getFontStyle(annotation.label_font, annotation.label_size);
  const labelText = annotation.label_text || 'Potential Issue';
  const lawRefs = annotation.law_references || [];
  
  // Parse label into issue text and law citation
  // Format: "Issue description — FDCPA §1692x" or just "Issue description"
  const labelParts = labelText.split(' — ');
  const issueText = labelParts[0] || labelText;
  const lawFromLabel = labelParts[1] || '';
  
  // Build law citation line - NEVER truncate
  let lawCitation = lawFromLabel;
  if (!lawCitation && lawRefs.length > 0) {
    lawCitation = lawRefs.map(ref => ref.replace('FDCPA ', 'FDCPA §').replace('1692', '1692')).join(', ');
  }
  
  // Calculate dimensions for multi-line label
  const charWidth = fontStyle.fontSize * 0.52;
  const maxLineWidth = 220;
  const maxCharsPerLine = Math.floor(maxLineWidth / charWidth);
  
  // Wrap issue text if needed (but keep it readable)
  let issueLine1 = issueText;
  let issueLine2 = '';
  if (issueText.length > maxCharsPerLine) {
    // Find a good break point
    const breakPoint = issueText.lastIndexOf(' ', maxCharsPerLine);
    if (breakPoint > maxCharsPerLine * 0.5) {
      issueLine1 = issueText.substring(0, breakPoint);
      issueLine2 = issueText.substring(breakPoint + 1);
    } else {
      issueLine1 = issueText.substring(0, maxCharsPerLine);
      issueLine2 = issueText.substring(maxCharsPerLine);
    }
  }
  
  // Calculate label dimensions
  const lineHeight = fontStyle.fontSize * 1.3;
  const numLines = (issueLine2 ? 2 : 1) + (lawCitation ? 1 : 0);
  const labelWidth = Math.max(
    issueLine1.length * charWidth,
    issueLine2 ? issueLine2.length * charWidth : 0,
    lawCitation ? lawCitation.length * charWidth : 0
  ) + 10;
  const labelHeight = lineHeight * numLines + 4;

  // Arrow coordinates
  const arrowStartX = scale(arrowFrom.x || labelX - 5);
  const arrowStartY = scale(arrowFrom.y || labelY + labelHeight / 2);
  const arrowEndX = scale(arrowTo.x || boxX + boxWidth + 3);
  const arrowEndY = scale(arrowTo.y || boxY + boxHeight / 2);

  const opacity = annotation.reviewer_status === 'dismissed' ? 0.3 : 1;
  
  // Check if this is a text_note type - these should NOT have a container shape
  const isTextNote = annotation.annotation_type === 'text_note';
  // Check if this is an arrow-only annotation (no target rectangle needed)
  const isArrowOnly = annotation.annotation_type === 'arrow';
  
  // Highlight pulse class for "Click to locate" feature
  const highlightClass = isHighlighted ? 'annotation-highlight-pulse' : '';

  return (
    <g 
      className={`annotation-group ${isSelected ? 'selected' : ''} ${highlightClass}`}
      style={{ 
        opacity
      }}
    >
      {/* Highlight glow behind rectangle when locating */}
      {isHighlighted && !isTextNote && !isArrowOnly && (
        <rect
          x={boxX - 4}
          y={boxY - 4}
          width={boxWidth + 8}
          height={boxHeight + 8}
          fill="#FBBF24"
          fillOpacity={0.35}
          stroke="#F59E0B"
          strokeWidth={3}
          rx={4}
          ry={4}
          className="annotation-highlight-glow"
          style={{ pointerEvents: 'none' }}
        />
      )}
      
      {/* Target rectangle - only render if not text_note or arrow-only */}
      {/* Dragging the rectangle moves the whole annotation */}
      {/* DIRECT EDIT: Rectangle is always clickable to select, regardless of tool */}
      {!isTextNote && !isArrowOnly && (
        <rect
          x={boxX}
          y={boxY}
          width={boxWidth}
          height={boxHeight}
          fill={isHighlighted ? 'rgba(251, 191, 36, 0.15)' : 'transparent'}
          stroke={isHighlighted ? '#B45309' : strokeColor}
          strokeWidth={isHighlighted ? 3 : (isStructural ? 1.5 : 2)}
          strokeDasharray={isStructural ? '6,3' : 'none'}
          rx={2}
          ry={2}
          style={{
            cursor: isEditMode ? (isSelected ? 'move' : 'pointer') : 'pointer',
            filter: isSelected || isHighlighted ? `drop-shadow(0 0 ${isHighlighted ? '8px #F59E0B' : '3px ' + strokeColor})` : 'none'
          }}
          onMouseDown={handleRectMouseDown}
        />
      )}

      {/* Selection indicator for rectangle */}
      {isSelected && isEditMode && !isTextNote && (
        <rect
          x={boxX - 2}
          y={boxY - 2}
          width={boxWidth + 4}
          height={boxHeight + 4}
          fill="transparent"
          stroke="#10b981"
          strokeWidth={2}
          strokeDasharray="4,4"
          rx={6}
          ry={6}
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Arrow line - smaller, cleaner arrow */}
      {!isTextNote && (
        <line
          x1={arrowStartX}
          y1={arrowStartY}
          x2={arrowEndX}
          y2={arrowEndY}
          stroke={strokeColor}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
          markerEnd="url(#clmArrowHead-small)"
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Invisible hit area for label dragging */}
      {/* DIRECT LABEL EDIT: Always shows interactive cursor in edit mode */}
      <rect
        x={labelX - 4}
        y={labelY - 2}
        width={labelWidth + 8}
        height={labelHeight + 4}
        fill="transparent"
        stroke="none"
        style={{ 
          cursor: isEditMode ? 'grab' : 'pointer',
          pointerEvents: 'all'
        }}
        onMouseDown={handleLabelMouseDown}
      />

      {/* Selection indicator for label (when selected and in edit mode) */}
      {isSelected && isEditMode && (
        <rect
          x={labelX - 4}
          y={labelY - 2}
          width={labelWidth + 8}
          height={labelHeight + 4}
          fill="rgba(16, 185, 129, 0.08)"
          stroke="#10b981"
          strokeWidth={1.5}
          strokeDasharray="3,3"
          rx={3}
          ry={3}
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Label text - MULTI-LINE, PLAIN RED TEXT, NO BOX */}
      {/* Full law citation MUST be visible - NO truncation */}
      {/* Label is independently draggable */}
      <text
        x={labelX}
        y={labelY}
        fill={strokeColor}
        style={{
          fontSize: fontStyle.fontSize,
          fontWeight: '700',
          fontFamily: fontStyle.fontFamily,
          pointerEvents: 'none'  // Click hits the invisible rect above
        }}
      >
        {/* Line 1: Issue text (first part) */}
        <tspan x={labelX} dy={fontStyle.fontSize}>{issueLine1}</tspan>
        {/* Line 2: Issue text continued (if wrapped) */}
        {issueLine2 && (
          <tspan x={labelX} dy={lineHeight}>{issueLine2}</tspan>
        )}
        {/* Law Citation Line - NEVER truncated */}
        {lawCitation && (
          <tspan x={labelX} dy={lineHeight} style={{ fontSize: fontStyle.fontSize * 0.9 }}>
            {lawCitation}
          </tspan>
        )}
      </text>

      {/* Source indicator */}
      {annotation.source === 'manual' && (
        <circle
          cx={labelX + labelWidth - 20}
          cy={labelY + labelHeight / 2}
          r={4}
          fill="#f59e0b"
          title="Manual annotation"
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* Resize handles (only when selected in edit mode) */}
      {isSelected && isEditMode && activeTool === TOOL_TYPES.SELECT && !isTextNote && (
        <g className="resize-handles">
          {['nw', 'ne', 'sw', 'se', 'n', 's', 'e', 'w'].map(pos => (
            <ResizeHandle
              key={pos}
              position={pos}
              boxX={boxX}
              boxY={boxY}
              boxWidth={boxWidth}
              boxHeight={boxHeight}
              onMouseDown={handleResizeStart}
            />
          ))}
        </g>
      )}

      {/* Arrow endpoint handles (only when selected) */}
      {isSelected && isEditMode && activeTool === TOOL_TYPES.SELECT && (
        <>
          <ArrowEndpointHandle
            x={arrowStartX}
            y={arrowStartY}
            type="from"
            onMouseDown={handleArrowEndpointStart}
          />
          <ArrowEndpointHandle
            x={arrowEndX}
            y={arrowEndY}
            type="to"
            onMouseDown={handleArrowEndpointStart}
          />
        </>
      )}
    </g>
  );
};

/**
 * Main Interactive Annotation Overlay Component
 */
const InteractiveAnnotationOverlay = ({
  annotations = [],
  pageWidth = 612,
  pageHeight = 792,
  renderWidth,
  renderHeight,
  zoom = 1,
  currentPage = 1,
  selectedAnnotationId,
  highlightedFindingId = null,
  onAnnotationSelect,
  onAnnotationUpdate,
  onAnnotationDelete,
  showAnnotations = true,
  isEditMode = false,
  activeTool = TOOL_TYPES.SELECT,
  onCreateAnnotation,
  textBlocks = []
}) => {
  const svgRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawStart, setDrawStart] = useState(null);
  const [drawCurrent, setDrawCurrent] = useState(null);

  // Scale factor converts from PDF page coordinates to render coordinates
  // This is independent of CSS zoom - the SVG scales with its container
  const scaleFactor = renderWidth ? renderWidth / pageWidth : 1;
  const svgWidth = renderWidth || pageWidth;
  const svgHeight = renderHeight || pageHeight;

  // Filter annotations for current page
  const pageAnnotations = annotations.filter(
    a => (a.page_number || 1) === currentPage && a.reviewer_status !== 'dismissed'
  );

  // Handle click on SVG background (deselect)
  const handleBackgroundClick = (e) => {
    if (e.target === svgRef.current) {
      onAnnotationSelect(null);
    }
  };

  // Handle drawing new annotations
  // ZOOM-AWARE: Mouse coordinates must account for CSS zoom transform
  const handleMouseDown = (e) => {
    if (!isEditMode) return;
    if (![TOOL_TYPES.RECTANGLE, TOOL_TYPES.DASHED_RECTANGLE, TOOL_TYPES.ARROW, TOOL_TYPES.TEXT_NOTE].includes(activeTool)) return;

    const rect = svgRef.current.getBoundingClientRect();
    // Convert screen position to document coordinates, accounting for both scaleFactor and zoom
    const x = (e.clientX - rect.left) / (scaleFactor * zoom);
    const y = (e.clientY - rect.top) / (scaleFactor * zoom);

    setIsDrawing(true);
    setDrawStart({ x, y });
    setDrawCurrent({ x, y });
  };

  const handleMouseMove = (e) => {
    if (!isDrawing || !drawStart) return;

    const rect = svgRef.current.getBoundingClientRect();
    // Convert screen position to document coordinates, accounting for both scaleFactor and zoom
    const x = (e.clientX - rect.left) / (scaleFactor * zoom);
    const y = (e.clientY - rect.top) / (scaleFactor * zoom);

    setDrawCurrent({ x, y });
  };

  const handleMouseUp = (e) => {
    if (!isDrawing || !drawStart || !drawCurrent) {
      setIsDrawing(false);
      return;
    }

    const x = Math.min(drawStart.x, drawCurrent.x);
    const y = Math.min(drawStart.y, drawCurrent.y);
    const width = Math.abs(drawCurrent.x - drawStart.x);
    const height = Math.abs(drawCurrent.y - drawStart.y);

    // Only create if size is meaningful
    if (width > 20 || height > 20 || activeTool === TOOL_TYPES.ARROW) {
      const newAnnotation = {
        id: `manual_${Date.now()}`,
        source: 'manual',
        annotation_type: activeTool === TOOL_TYPES.DASHED_RECTANGLE ? 'dashed_rectangle' : 
                         activeTool === TOOL_TYPES.ARROW ? 'arrow' : 
                         activeTool === TOOL_TYPES.TEXT_NOTE ? 'text_note' : 'rectangle',
        page_number: currentPage,
        target_region: {
          x: x,
          y: y,
          width: Math.max(width, MIN_WIDTH),
          height: Math.max(height, MIN_HEIGHT)
        },
        label_position: {
          x: x + width + 15,
          y: y + 5
        },
        arrow_from: {
          x: x + width + 10,
          y: y + height / 2 + 15
        },
        arrow_to: {
          x: x + width + 3,
          y: y + height / 2
        },
        label_text: 'New finding - click to edit',
        label_font: 'legal_sans',
        label_size: 'standard',
        severity: 'medium',
        law_references: [],
        reviewer_status: 'auto_flagged',
        annotation_scope: activeTool === TOOL_TYPES.DASHED_RECTANGLE ? 'structural' : 'sentence',
        created_by: 'user',
        edited_by_user: true,
        edit_type: 'manual_addition',
        updated_at: new Date().toISOString()
      };

      onCreateAnnotation(newAnnotation);
    }

    setIsDrawing(false);
    setDrawStart(null);
    setDrawCurrent(null);
  };

  // Drawing preview
  const drawingPreview = isDrawing && drawStart && drawCurrent && (
    <rect
      x={Math.min(drawStart.x, drawCurrent.x) * scaleFactor}
      y={Math.min(drawStart.y, drawCurrent.y) * scaleFactor}
      width={Math.abs(drawCurrent.x - drawStart.x) * scaleFactor}
      height={Math.abs(drawCurrent.y - drawStart.y) * scaleFactor}
      fill="rgba(16, 185, 129, 0.1)"
      stroke="#10b981"
      strokeWidth={2}
      strokeDasharray="8,4"
      rx={4}
      ry={4}
    />
  );

  if (!showAnnotations && !isEditMode) {
    return null;
  }

  return (
    <svg
      ref={svgRef}
      width={svgWidth}
      height={svgHeight}
      className="absolute top-0 left-0"
      style={{ 
        zIndex: 10, 
        overflow: 'visible',
        pointerEvents: isEditMode ? 'all' : 'none'
      }}
      onClick={handleBackgroundClick}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      data-testid="interactive-annotation-overlay"
    >
      {/* Arrow markers - SMALLER, CLEANER */}
      <defs>
        {/* Small red arrow marker - precise, not oversized */}
        <marker
          id="clmArrowHead-small"
          markerWidth="6"
          markerHeight="6"
          refX="5"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={ANNOTATION_COLOR} />
        </marker>
        {/* Legacy markers - all small now */}
        <marker
          id="clmArrowHead-red"
          markerWidth="6"
          markerHeight="6"
          refX="5"
          refY="3"
          orient="auto"
        >
          <path d="M0,0 L6,3 L0,6 Z" fill={ANNOTATION_COLOR} />
        </marker>
        {['high', 'medium', 'low'].map(severity => (
          <marker
            key={severity}
            id={`clmArrowHead-${severity}`}
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
          >
            <path d="M0,0 L6,3 L0,6 Z" fill={ANNOTATION_COLOR} />
          </marker>
        ))}
      </defs>

      {/* Existing annotations */}
      <g className="annotations-layer" style={{ pointerEvents: 'all' }}>
        {pageAnnotations.map((annotation, idx) => {
          const annotationId = annotation.id || annotation.finding_id || annotation.normalized_id;
          const shouldHighlight = highlightedFindingId && (
            annotationId === highlightedFindingId ||
            annotation.finding_id === highlightedFindingId ||
            annotation.normalized_id === highlightedFindingId
          );
          
          return (
            <InteractiveAnnotationItem
              key={annotation.id || `annotation-${idx}`}
              annotation={annotation}
              isSelected={selectedAnnotationId === annotation.id}
              isHighlighted={shouldHighlight}
              isEditMode={isEditMode}
              scaleFactor={scaleFactor}
              zoom={zoom}
              onSelect={onAnnotationSelect}
              onUpdate={onAnnotationUpdate}
              onDelete={onAnnotationDelete}
              activeTool={activeTool}
            />
          );
        })}
      </g>

      {/* Drawing preview */}
      {drawingPreview}
    </svg>
  );
};

export default InteractiveAnnotationOverlay;
